import { all, call, takeLeading, put, takeLatest } from '@redux-saga/core/effects';
import axios from 'axios';

import { ERROR, SUCCESS } from '../../utils/constants';
import { getErrorMessage } from '../../utils/api-util';
import { retrieveFromLocalStorage } from '../../utils/util';

import {
  requestViewPersonContracts,
  requestViewContract,
  requestExpressInterest,
  requestAmendContract,
  requestUpdateContractStatus,
  requestCancelContract,
  requestRetrieveChatForDeal,
  requestCreateMessageForDeal,
  requestViewAdminContracts,
  requestReviewDocument,
  requestRequestAssistance,
} from './contract.actions';
import {
  getViewPersonContractsRequest,
  getViewContractRequest,
  getExpressInterestRequest,
  getAmendContractRequest,
  getUpdateContractStatusRequest,
  getCancelContractRequest,
  getRetrieveChatForDealRequest,
  getCreateMessageForDealRequest,
  getViewAdminContractsRequest,
  getReviewDocumentRequest,
  getRequestAssistanceRequest,
} from './contract.endpoints';

import { addSystemNotice, addSpinner, removeSpinner } from '../../slices/system/system.slice';
import { setContracts, setContract, setChats, setAdminContracts } from '../../slices/contract/contract.slice';

export function* performViewPersonContractsRequest() {
  try {
    yield put(addSpinner('Retrieving Contracts'));

    const [endpoint, options] = getViewPersonContractsRequest();

    const { data } = yield call(axios, endpoint, options);

    yield put(setContracts(data.contracts));
  } catch (error) {
    yield put(setContracts([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Contracts'));
  }
}

export function* watchForViewPersonContractsRequest() {
  yield takeLeading(requestViewPersonContracts.type, performViewPersonContractsRequest);
}

export function* performViewContractRequest({ payload }) {
  const { contractExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Contract'));

    const [endpoint, options] = getViewContractRequest(contractExternalReference);

    const { data } = yield call(axios, endpoint, options);

    delete data.message;

    yield put(setContract(data));

    if (onSuccess) {
      onSuccess(data);
    }
  } catch (error) {
    yield put(setContract(undefined));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Contract'));
  }
}

export function* watchForViewContractRequest() {
  yield takeLatest(requestViewContract.type, performViewContractRequest);
}

export function* performExpressInterestRequest({ payload }) {
  const { productExternalReference, onSuccess } = payload;

  try {
    yield put(addSpinner('Expressing Interest'));

    const [endpoint, options] = getExpressInterestRequest(productExternalReference);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess(data.contractExternalReference);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Expressing Interest'));
  }
}

export function* watchForExpressInterestRequest() {
  yield takeLeading(requestExpressInterest.type, performExpressInterestRequest);
}

export function* performAmendContractRequest({ payload }) {
  const { contract, onSuccess } = payload;

  try {
    yield put(addSpinner('Amending Contract'));

    const [endpoint, options] = getAmendContractRequest(contract);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Amending Contract'));
  }
}

export function* watchForAmendContractRequest() {
  yield takeLeading(requestAmendContract.type, performAmendContractRequest);
}

export function* performUpdateContractStatusRequest({ payload }) {
  const { dealNumber, status, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Contract'));

    const [endpoint, options] = getUpdateContractStatusRequest(dealNumber, status);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Contract'));
  }
}

export function* watchForUpdateContractStatusRequest() {
  yield takeLeading(requestUpdateContractStatus.type, performUpdateContractStatusRequest);
}

export function* performCancelContractRequest({ payload }) {
  const { dealNumber, onSuccess } = payload;

  try {
    yield put(addSpinner('Cancelling Contract'));

    const [endpoint, options] = getCancelContractRequest(dealNumber);

    const { data } = yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }

    yield put(addSystemNotice({ type: SUCCESS, message: data.message }));
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Cancelling Contract'));
  }
}

export function* watchForCancelContractRequest() {
  yield takeLeading(requestCancelContract.type, performCancelContractRequest);
}

export function* performRetrieveChatForDealRequest({ payload }) {
  const { dealNumber, showSpinner } = payload;

  try {
    if (showSpinner) {
      yield put(addSpinner('Retrieving Messages'));
    }

    const personExternalReference = retrieveFromLocalStorage('personExternalReference');
    const [endpoint, options] = getRetrieveChatForDealRequest(dealNumber, personExternalReference);

    const { data } = yield call(axios, endpoint, options);

    yield put(setChats(data.chats));
  } catch (error) {
    yield put(setChats([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    if (showSpinner) {
      yield put(removeSpinner('Retrieving Messages'));
    }
  }
}

export function* watchForRetieveChatForDealRequest() {
  yield takeLatest(requestRetrieveChatForDeal.type, performRetrieveChatForDealRequest);
}

export function* performCreateMessageForDeal({ payload }) {
  const { message, onSuccess } = payload;

  try {
    yield put(addSpinner('Sending Message'));

    const [endpoint, options] = getCreateMessageForDealRequest(message);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      onSuccess();
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Sending Message'));
  }
}

export function* watchForCreateMessageForDealRequest() {
  yield takeLeading(requestCreateMessageForDeal.type, performCreateMessageForDeal);
}

export function* performViewAdminContractsRequest({ payload }) {
  const { filters, onSuccess } = payload;

  try {
    yield put(addSpinner('Retrieving Contracts'));

    const [endpoint, options] = getViewAdminContractsRequest(filters);

    const { data } = yield call(axios, endpoint, options);
    const { contractsForAdmins, totalContracts } = data;

    if (onSuccess) {
      yield call(onSuccess, totalContracts);
    }

    yield put(setAdminContracts(contractsForAdmins));
  } catch (error) {
    yield put(setAdminContracts([]));
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Retrieving Contracts'));
  }
}

export function* watchForViewAdminContractsRequest() {
  yield takeLeading(requestViewAdminContracts.type, performViewAdminContractsRequest);
}

export function* performReviewDocumentRequest({ payload }) {
  const { document, onSuccess } = payload;

  try {
    yield put(addSpinner('Updating Document'));

    const [endpoint, options] = getReviewDocumentRequest(document);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Updating Document'));
  }
}

export function* watchForReviewDocumentRequest() {
  yield takeLeading(requestReviewDocument.type, performReviewDocumentRequest);
}

export function* performRequestAssistanceRequest({ payload }) {
  const { onSuccess, data } = payload;

  try {
    yield put(addSpinner('Resolving Assistance'));

    const [endpoint, options] = getRequestAssistanceRequest(data);

    yield call(axios, endpoint, options);

    if (onSuccess) {
      yield call(onSuccess);
    }
  } catch (error) {
    const message = getErrorMessage(error);
    yield put(addSystemNotice({ type: ERROR, message }));
  } finally {
    yield put(removeSpinner('Resolving Assistance'));
  }
}

export function* watchForRequestAssistanceRequest() {
  yield takeLeading(requestRequestAssistance.type, performRequestAssistanceRequest);
}

export default function* contractSaga() {
  yield all([
    watchForViewPersonContractsRequest(),
    watchForViewContractRequest(),
    watchForExpressInterestRequest(),
    watchForAmendContractRequest(),
    watchForUpdateContractStatusRequest(),
    watchForCancelContractRequest(),
    watchForRetieveChatForDealRequest(),
    watchForCreateMessageForDealRequest(),
    watchForViewAdminContractsRequest(),
    watchForReviewDocumentRequest(),
    watchForRequestAssistanceRequest(),
  ]);
}
